import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";
import CTABlock from "../components/articles/ctaBlock";
import AboutBuilder from "../components/common/aboutBuilder";
import ShareBar from "../components/common/shareBar";
import { getArtifactMeta } from "../components/articles/artifacts";
import { getArticleBySlug } from "../data/articles";
import { track } from "../lib/analytics";
import Notfound from "./404";

import INFO from "../data/user";

import "./styles/tool.css";

// Standalone page for a single interactive tool, so each tool has its own clean,
// shareable URL (/tool/:key) instead of only living inside an article. The tool
// itself is the same component rendered in the article; this page adds a header,
// a link back to the full write-up, and the newsletter CTA.
const Tool = () => {
	const { key } = useParams();
	const meta = getArtifactMeta(key);

	useEffect(() => {
		window.scrollTo(0, 0);
		if (meta) {
			track("tool-view", { tool: key });
		}
	}, [key, meta]);

	if (!meta) {
		return <Notfound />;
	}

	const Artifact = meta.component;
	const sourceArticle = getArticleBySlug(meta.articleSlug);

	// Absolute URL and share image for OpenGraph / Twitter, so this tool unfurls
	// with its own title and description when shared. The post-build prerender
	// script bakes the same tags into static HTML for crawlers.
	const SITE = "https://swapniltamse.com";
	const pageUrl = `${SITE}/tool/${key}`;
	const ogImage = `${SITE}/homepage.jpg`;

	return (
		<React.Fragment>
			<Helmet>
				<title>{`${meta.title} | ${INFO.main.title}`}</title>
				<meta name="description" content={meta.blurb} />
				<link rel="canonical" href={pageUrl} />
				<meta property="og:type" content="website" />
				<meta property="og:url" content={pageUrl} />
				<meta property="og:title" content={meta.title} />
				<meta property="og:description" content={meta.blurb} />
				<meta property="og:image" content={ogImage} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={meta.title} />
				<meta name="twitter:description" content={meta.blurb} />
				<meta name="twitter:image" content={ogImage} />
			</Helmet>

			<div className="page-content">
				<NavBar active="tools" />

				<div className="content-wrapper">
					<div className="tool-logo-container">
						<div className="tool-logo">
							<Logo width={46} />
						</div>
					</div>

					<div className="tool-container">
						<div className="tool-eyebrow">
							<Link to="/tools">Tools</Link>
							<span className="tool-eyebrow-dot">•</span>
							<span>{meta.topic}</span>
						</div>

						<div className="title tool-title">{meta.title}</div>

						<div className="subtitle tool-blurb">{meta.blurb}</div>

						<div className="tool-artifact">
							<Artifact />
						</div>

						{sourceArticle && (
							<div className="tool-source">
								<Link to={`/article/${meta.articleSlug}`}>
									Read the thinking behind this tool:{" "}
									{sourceArticle.title} →
								</Link>
							</div>
						)}

						<ShareBar title={meta.title} kind="tool" />

						<CTABlock />

						<AboutBuilder context="Most of these tools started as a question I wanted answered, then turned into something you can use in five minutes." />
					</div>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Tool;
